import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { articles, type Article } from '@/mocks/articles';
import { useSeo } from '@/lib/seo';
import { useJsonLd, buildArticleSchema } from '@/lib/jsonld';

type Lang = 'en' | 'pt' | 'de';

function resolveLang(lang: string): Lang {
  if (lang === 'pt' || lang === 'de') return lang;
  return 'en';
}

export default function ArticleTemplate() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation(['common']);
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState('');

  const article: Article | undefined = articles.find((a) => a.slug === slug);
  const lang = resolveLang(i18n.language);

  // Scroll to top whenever the article changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    setActiveId('');
  }, [slug]);

  // SEO head management
  useSeo({
    title: article ? article.metaTitle[lang] : 'CSSBuy Spreadsheet',
    description: article ? article.metaDesc[lang] : undefined,
    canonical: article ? `/article/${article.slug}` : undefined,
    ogType: 'article',
    ogImage: article?.image,
  });

  useJsonLd(
    article
      ? buildArticleSchema({
          title: article.title[lang],
          description: article.metaDesc[lang],
          image: article.image,
          url: `/article/${article.slug}`,
        })
      : null
  );

  // Observe headings for the table of contents highlight
  useEffect(() => {
    if (!article) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-80px 0px -60% 0px' }
    );
    article.sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Article not found</h1>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors cursor-pointer"
          >
            {t('common:breadcrumb.home')}
          </button>
        </div>
      </div>
    );
  }

  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 4);

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm flex-wrap">
            <Link to="/" className="text-gray-500 hover:text-emerald-700 transition-colors">
              {t('common:breadcrumb.home')}
            </Link>
            <i className="ri-arrow-right-s-line text-gray-300 text-xs" />
            <span className="text-gray-400">{t('common:breadcrumb.articles')}</span>
            <i className="ri-arrow-right-s-line text-gray-300 text-xs" />
            <span className="text-gray-600 line-clamp-1">{article.tag[lang]}</span>
          </nav>
        </div>
      </div>

      {/* Article Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={article.image}
            alt={article.title[lang]}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/65" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className={`px-3 py-1 rounded-full bg-white/20 text-white text-xs font-medium`}>
                {article.tag[lang]}
              </span>
              <span className="text-gray-300 text-sm">{article.readTime[lang]}</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              {article.title[lang]}
            </h1>
            <p className="text-gray-200 text-sm md:text-base leading-relaxed">
              {article.excerpt[lang]}
            </p>
          </div>
        </div>
      </div>

      {/* Article Body */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-8">
            {/* Author info */}
            <div className="flex items-center gap-3 mb-10 pb-6 border-b border-gray-100">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm">
                C
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">CSSBuy Research Team</p>
                <p className="text-xs text-gray-400">
                  {t('common:article.cssbuyGuide.date')} • {article.readTime[lang]}
                </p>
              </div>
            </div>

            <div className="space-y-8">
              <p className="text-gray-600 leading-relaxed text-base">{article.intro[lang]}</p>

              {article.sections.map((section) => (
                <section key={section.id} id={section.id}>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 scroll-mt-24">
                    {section.heading[lang]}
                  </h2>
                  <p className="text-gray-600 leading-relaxed text-base">{section.body[lang]}</p>
                </section>
              ))}

              <section>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 scroll-mt-24">
                  {lang === 'pt' ? 'Conclusão' : lang === 'de' ? 'Fazit' : 'Conclusion'}
                </h2>
                <p className="text-gray-600 leading-relaxed text-base">{article.conclusion[lang]}</p>
              </section>
            </div>

            {/* Internal link back to home */}
            <div className="mt-10 pt-8 border-t border-gray-100">
              <div className="bg-emerald-50 rounded-xl p-6 md:p-8">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {lang === 'pt' ? 'Voltar ao Início' : lang === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {lang === 'pt'
                    ? 'Explore mais recursos, categorias e guias na página inicial do CSSBuy Spreadsheet.'
                    : lang === 'de'
                    ? 'Entdecken Sie weitere Ressourcen, Kategorien und Guides auf der CSSBuy Spreadsheet Startseite.'
                    : 'Explore more resources, categories, and guides on the CSSBuy Spreadsheet homepage.'}
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors"
                >
                  {t('common:cta.explore')}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>

            {/* Related Articles (internal links) */}
            <div className="mt-10">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {lang === 'pt' ? 'Artigos Relacionados' : lang === 'de' ? 'Verwandte Artikel' : 'Related Articles'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {related.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group block p-4 rounded-xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all"
                  >
                    <p className="text-sm font-medium text-gray-900 group-hover:text-emerald-700 transition-colors">
                      {rel.title[lang]}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{rel.readTime[lang]}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4">
            {/* Table of contents */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 sticky top-24">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                {lang === 'pt' ? 'Índice' : lang === 'de' ? 'Inhaltsverzeichnis' : 'Table of Contents'}
              </h3>
              <ul className="space-y-2">
                {article.sections.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className={`block text-sm transition-colors ${
                        activeId === item.id
                          ? 'text-emerald-700 font-medium'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {item.heading[lang]}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA Box */}
            <div className="mt-6 bg-gray-900 rounded-xl p-6 text-white">
              <h3 className="font-semibold mb-2">CSSBuy Spreadsheet</h3>
              <p className="text-sm text-gray-300 mb-4">
                {lang === 'pt'
                  ? 'Pronto para começar a comprar? Visite o site principal para o catálogo completo.'
                  : lang === 'de'
                  ? 'Bereit zum Einkaufen? Besuchen Sie die Hauptwebsite für den vollständigen Katalog.'
                  : 'Ready to start shopping? Visit the main website for the full product catalog.'}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors"
              >
                {t('common:cta.visitMain')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}